///
/// EarthBrowser
///
/// Created by Matt Giger on 11/27/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Geometry } from "@stovokor/shader/Geometry"
import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { vec3, cross, normal } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

export interface SelectionSphereOptions {
	radius?: number
	sphereSegments?: number
	arrowWidth?: number
	arrowArcAngle?: number
	arrowheadLength?: number
	arrowheadWidth?: number
	arrowOffset?: number
	// When true, raises the horizontal arrows to ~15° latitude for visibility at ground level
	elevateEquator?: boolean
	// Colors for different parts (RGB 0-1)
	horizontalColor?: vec3
	verticalColor?: vec3
	zoomColor?: vec3
	sphereColor?: vec3
}

export class SelectionSphereGeometry implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public color: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, options: SelectionSphereOptions = {}) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.color = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.color])

		const {
			radius = 1.0,
			sphereSegments = 32,
			arrowWidth = 0.035, // Reduced width for cleaner look
			arrowArcAngle = Math.PI * 0.3, // Shortened to 50% of previous (was 0.6)
			arrowheadLength = 0.12,
			arrowheadWidth = 0.09,
			arrowOffset = 0.02, // How far above sphere surface
			elevateEquator = false, // When true, raises horizontal arrows 15° for ground-level visibility
			// Design system aligned colors (brand: blue→cyan→teal)
			horizontalColor = new vec3(0.02, 0.71, 0.83), // cyan-500 (#06b6d4)
			verticalColor = new vec3(0.08, 0.72, 0.65), // teal-500 (#14b8a6)
			zoomColor = new vec3(0.9, 0.9, 0.95), // Near white for neutral zoom indicator
			sphereColor = new vec3(1.0, 1.0, 1.0) // Pure white for HUD-style wireframe
		} = options

		const arrowRadius = radius + arrowOffset
		// Elevation angle for horizontal arrows (15 degrees in radians)
		const elevationAngle = elevateEquator ? Math.PI * 15 / 180 : 0

		// Create 3 horizontal rotation arrows (around Y axis, evenly spaced)
		const horizontalAngles = [0, Math.PI * 2 / 3, Math.PI * 4 / 3]
		for (const startAngle of horizontalAngles) {
			this.createCurvedArrow(
				arrowRadius,
				arrowWidth,
				arrowArcAngle,
				arrowheadLength,
				arrowheadWidth,
				horizontalColor,
				"horizontal",
				sphereSegments,
				startAngle,
				elevationAngle
			)
		}

		// Create 2 vertical rotation arrows (on a meridian)
		this.createCurvedArrow(
			arrowRadius,
			arrowWidth,
			arrowArcAngle,
			arrowheadLength,
			arrowheadWidth,
			verticalColor,
			"vertical",
			sphereSegments,
			0
		)
		this.createCurvedArrow(
			arrowRadius,
			arrowWidth,
			arrowArcAngle,
			arrowheadLength,
			arrowheadWidth,
			verticalColor,
			"vertical",
			sphereSegments,
			Math.PI
		)

		// Create zoom indicators (radial arrows pointing in/out)
		this.createZoomIndicators(
			radius,
			arrowWidth * 0.8,
			arrowheadLength * 0.7,
			arrowheadWidth * 0.8,
			zoomColor
		)

		// Create transparent sphere wireframe
		this.createSphereWireframe(radius, sphereSegments, sphereColor)

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	private createCurvedArrow(
		radius: number,
		width: number,
		arcAngle: number,
		headLength: number,
		headWidth: number,
		color: vec3,
		orientation: "horizontal" | "vertical",
		segments: number,
		startAngle: number,
		elevationAngle: number = 0 // Latitude offset for horizontal arrows
	): void {
		const arcSegments = Math.max(8, Math.floor(segments * (arcAngle / (Math.PI * 2))))
		const halfWidth = width / 2

		// Reduce arc slightly to make room for arrowheads
		const arrowheadAngle = headLength / radius
		const adjustedArcAngle = arcAngle - arrowheadAngle * 2
		const adjustedStartAngle = startAngle + arrowheadAngle

		const baseIndex = this.position.values.length / 3

		// For elevated horizontal arrows, calculate the Y offset and reduced ring radius
		const cosElev = Math.cos(elevationAngle)
		const sinElev = Math.sin(elevationAngle)
		const elevatedY = sinElev * radius
		const elevatedRadius = cosElev * radius

		// Create the curved ribbon body
		for (let i = 0; i <= arcSegments; i++) {
			const t = i / arcSegments
			const angle = adjustedStartAngle + t * adjustedArcAngle

			let center: vec3
			let tangent: vec3
			let outward: vec3

			if (orientation === "horizontal") {
				// Arrow on a latitude circle (elevated from equator if specified)
				center = new vec3(
					Math.cos(angle) * elevatedRadius,
					elevatedY,
					Math.sin(angle) * elevatedRadius
				)
				outward = normal(center)
				tangent = new vec3(-Math.sin(angle), 0, Math.cos(angle))
			} else {
				// Arrow on a meridian plane (XY plane, Z=0)
				center = new vec3(
					Math.cos(angle) * radius,
					Math.sin(angle) * radius,
					0
				)
				outward = normal(center)
				tangent = new vec3(-Math.sin(angle), Math.cos(angle), 0)
			}

			// Create inner and outer edge vertices
			const binormal = normal(cross(tangent, outward))
			const inner = center.subtract(binormal.multiply(halfWidth))
			const outer = center.add(binormal.multiply(halfWidth))

			this.position.addVec(inner)
			this.normal.addVec(outward)
			this.color.addVec(color)

			this.position.addVec(outer)
			this.normal.addVec(outward)
			this.color.addVec(color)

			// Create triangles
			if (i > 0) {
				const curr = baseIndex + i * 2
				const prev = curr - 2
				this.indexBuffer.triangle(prev, prev + 1, curr)
				this.indexBuffer.triangle(prev + 1, curr + 1, curr)
			}
		}

		// Create arrowhead at the end (pointing in direction of travel)
		this.createArrowhead(
			radius,
			adjustedStartAngle + adjustedArcAngle,
			headLength,
			headWidth,
			color,
			orientation,
			1, // Forward direction
			elevationAngle
		)

		// Create arrowhead at the start (pointing backward - for bidirectional)
		this.createArrowhead(
			radius,
			adjustedStartAngle,
			headLength,
			headWidth,
			color,
			orientation,
			-1, // Backward direction
			elevationAngle
		)
	}

	private createArrowhead(
		radius: number,
		angle: number,
		length: number,
		width: number,
		color: vec3,
		orientation: "horizontal" | "vertical",
		direction: number, // 1 = forward, -1 = backward
		elevationAngle: number = 0
	): void {
		const baseIndex = this.position.values.length / 3
		const halfWidth = width / 2
		const headAngle = length / radius * direction

		// For elevated horizontal arrows
		const cosElev = Math.cos(elevationAngle)
		const sinElev = Math.sin(elevationAngle)
		const elevatedY = sinElev * radius
		const elevatedRadius = cosElev * radius

		let tipPos: vec3
		let baseCenter: vec3
		let outward: vec3
		let binormal: vec3

		if (orientation === "horizontal") {
			const tipAngle = angle + headAngle
			tipPos = new vec3(
				Math.cos(tipAngle) * elevatedRadius,
				elevatedY,
				Math.sin(tipAngle) * elevatedRadius
			)
			baseCenter = new vec3(
				Math.cos(angle) * elevatedRadius,
				elevatedY,
				Math.sin(angle) * elevatedRadius
			)
			outward = normal(baseCenter)
			const tangent = new vec3(-Math.sin(angle), 0, Math.cos(angle))
			binormal = normal(cross(tangent, outward))
		} else {
			const tipAngle = angle + headAngle
			tipPos = new vec3(
				Math.cos(tipAngle) * radius,
				Math.sin(tipAngle) * radius,
				0
			)
			baseCenter = new vec3(
				Math.cos(angle) * radius,
				Math.sin(angle) * radius,
				0
			)
			outward = normal(baseCenter)
			const tangent = new vec3(-Math.sin(angle), Math.cos(angle), 0)
			binormal = normal(cross(tangent, outward))
		}

		const baseLeft = baseCenter.subtract(binormal.multiply(halfWidth))
		const baseRight = baseCenter.add(binormal.multiply(halfWidth))

		// Tip vertex
		this.position.addVec(tipPos)
		this.normal.addVec(normal(tipPos))
		this.color.addVec(color)

		// Base left
		this.position.addVec(baseLeft)
		this.normal.addVec(outward)
		this.color.addVec(color)

		// Base right
		this.position.addVec(baseRight)
		this.normal.addVec(outward)
		this.color.addVec(color)

		// Triangle (winding order depends on direction)
		if (direction > 0) {
			this.indexBuffer.triangle(baseIndex, baseIndex + 1, baseIndex + 2)
		} else {
			this.indexBuffer.triangle(baseIndex, baseIndex + 2, baseIndex + 1)
		}
	}

	private createZoomIndicators(
		radius: number,
		shaftWidth: number,
		headLength: number,
		headWidth: number,
		color: vec3
	): void {
		// Create 2 radial double-headed arrows at opposite diagonal positions
		const positions = [
			new vec3(1, 1, 1),
			new vec3(-1, -1, -1)
		]

		for (const dir of positions) {
			this.createRadialArrow(
				radius,
				normal(dir),
				shaftWidth,
				headLength,
				headWidth,
				color
			)
		}
	}

	private createRadialArrow(
		radius: number,
		direction: vec3,
		shaftWidth: number,
		headLength: number,
		headWidth: number,
		color: vec3
	): void {
		const baseIndex = this.position.values.length / 3
		const innerRadius = radius * 0.85
		const outerRadius = radius * 1.15

		// Get perpendicular vectors
		const perp = this.getPerpendicularVectors(direction)
		const halfShaftWidth = shaftWidth / 2
		const halfHeadWidth = headWidth / 2

		// Inner tip (pointing inward)
		const innerTip = direction.multiply(innerRadius - headLength)

		// Inner arrow base
		const innerBase = direction.multiply(innerRadius)
		const innerBaseLeft = innerBase.subtract(perp.perp1.multiply(halfHeadWidth))
		const innerBaseRight = innerBase.add(perp.perp1.multiply(halfHeadWidth))

		// Shaft inner end
		const shaftInner = direction.multiply(innerRadius)
		const shaftInnerLeft = shaftInner.subtract(perp.perp1.multiply(halfShaftWidth))
		const shaftInnerRight = shaftInner.add(perp.perp1.multiply(halfShaftWidth))

		// Shaft outer end
		const shaftOuter = direction.multiply(outerRadius)
		const shaftOuterLeft = shaftOuter.subtract(perp.perp1.multiply(halfShaftWidth))
		const shaftOuterRight = shaftOuter.add(perp.perp1.multiply(halfShaftWidth))

		// Outer arrow base
		const outerBase = direction.multiply(outerRadius)
		const outerBaseLeft = outerBase.subtract(perp.perp1.multiply(halfHeadWidth))
		const outerBaseRight = outerBase.add(perp.perp1.multiply(halfHeadWidth))

		// Outer tip (pointing outward)
		const outerTip = direction.multiply(outerRadius + headLength)

		// Add vertices and create triangles

		// Inner arrowhead
		this.position.addVec(innerTip)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(innerBaseRight)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(innerBaseLeft)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.indexBuffer.triangle(baseIndex, baseIndex + 1, baseIndex + 2)

		// Shaft (as a quad)
		const shaftBaseIndex = this.position.values.length / 3

		this.position.addVec(shaftInnerLeft)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(shaftInnerRight)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(shaftOuterRight)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(shaftOuterLeft)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.indexBuffer.quad(shaftBaseIndex, shaftBaseIndex + 1, shaftBaseIndex + 2, shaftBaseIndex + 3)

		// Outer arrowhead
		const outerBaseIndex = this.position.values.length / 3

		this.position.addVec(outerTip)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(outerBaseLeft)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.position.addVec(outerBaseRight)
		this.normal.addVec(perp.perp2)
		this.color.addVec(color)

		this.indexBuffer.triangle(outerBaseIndex, outerBaseIndex + 1, outerBaseIndex + 2)
	}

	private createSphereWireframe(
		radius: number,
		segments: number,
		color: vec3
	): void {
		const latLines = 10 // Doubled latitude line count for better coverage
		const lonLines = 8 // Number of longitude lines
		const lineWidth = 0.025 // Increased for more prominent sphere lines
		const smoothSegments = segments * 2 // Double segments for smoother lines

		// Create latitude circles (horizontal rings)
		for (let i = 1; i < latLines; i++) {
			const phi = (i / latLines) * Math.PI - Math.PI / 2
			const y = Math.sin(phi) * radius
			const ringRadius = Math.cos(phi) * radius
			this.createCircle(ringRadius, y, smoothSegments, lineWidth, color, "horizontal")
		}

		// Create longitude circles (vertical rings)
		for (let i = 0; i < lonLines; i++) {
			const theta = (i / lonLines) * Math.PI
			this.createMeridianArc(radius, theta, smoothSegments, lineWidth, color)
		}
	}

	private createCircle(
		radius: number,
		y: number,
		segments: number,
		lineWidth: number,
		color: vec3,
		_orientation: string
	): void {
		const baseIndex = this.position.values.length / 3
		const halfWidth = lineWidth / 2

		for (let i = 0; i <= segments; i++) {
			const theta = (i / segments) * Math.PI * 2
			const x = Math.cos(theta) * radius
			const z = Math.sin(theta) * radius

			const center = new vec3(x, y, z)
			const horizontalNormal = normal(new vec3(x, 0, z))
			const upNormal = new vec3(0, 1, 0)

			// Inner and outer edge
			const inner = center.subtract(horizontalNormal.multiply(halfWidth))
			const outer = center.add(horizontalNormal.multiply(halfWidth))

			this.position.addVec(inner)
			this.normal.addVec(upNormal)
			this.color.addVec(color)

			this.position.addVec(outer)
			this.normal.addVec(upNormal)
			this.color.addVec(color)

			if (i > 0) {
				const curr = baseIndex + i * 2
				const prev = curr - 2
				this.indexBuffer.triangle(prev, curr, prev + 1)
				this.indexBuffer.triangle(prev + 1, curr, curr + 1)
			}
		}
	}

	private createMeridianArc(
		radius: number,
		theta: number,
		segments: number,
		lineWidth: number,
		color: vec3
	): void {
		const baseIndex = this.position.values.length / 3
		const halfWidth = lineWidth / 2
		const cosTheta = Math.cos(theta)
		const sinTheta = Math.sin(theta)

		for (let i = 0; i <= segments; i++) {
			const phi = (i / segments) * Math.PI * 2
			const cosPhi = Math.cos(phi)
			const sinPhi = Math.sin(phi)

			const x = cosPhi * cosTheta * radius
			const y = sinPhi * radius
			const z = cosPhi * sinTheta * radius

			const center = new vec3(x, y, z)
			const outwardNormal = normal(center)

			// Tangent along the meridian
			const tangent = new vec3(-sinPhi * cosTheta, cosPhi, -sinPhi * sinTheta)
			const binormal = normal(cross(tangent, outwardNormal))

			// Inner and outer edge
			const inner = center.subtract(binormal.multiply(halfWidth))
			const outer = center.add(binormal.multiply(halfWidth))

			this.position.addVec(inner)
			this.normal.addVec(outwardNormal)
			this.color.addVec(color)

			this.position.addVec(outer)
			this.normal.addVec(outwardNormal)
			this.color.addVec(color)

			if (i > 0) {
				const curr = baseIndex + i * 2
				const prev = curr - 2
				this.indexBuffer.triangle(prev, curr, prev + 1)
				this.indexBuffer.triangle(prev + 1, curr, curr + 1)
			}
		}
	}

	private getPerpendicularVectors(direction: vec3): { perp1: vec3; perp2: vec3 } {
		let perp1: vec3
		if (Math.abs(direction.x) < 0.9) {
			perp1 = normal(cross(vec3.unitX, direction))
		} else {
			perp1 = normal(cross(vec3.unitY, direction))
		}
		const perp2 = normal(cross(direction, perp1))
		return { perp1, perp2 }
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

